import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import { AlertTriangle, Lock, Pencil, Trash2 } from 'lucide-react';
import { Header } from '../Components/Header/Header';
import { ColumnDef, DataTable } from '../Components/Table/DataTable';
import { LocationSelect } from '../Components/Select/LocationSelect';
import ConfirmationModal from '../Components/Modal/ConfirmationModal';
import useGetCustomData from '../Hooks/useGetCustomData';
import { useColumnSearches } from '../Hooks/useColumnSearch';
import { SearchableHeader } from '../Components/Search/SearchableHeader';
import apiClient from '../Services/ApiClient';
import { decode } from '../Services/DecodeToken';
import { USER_ROLE } from '../Types';
import { PageContainer } from '../Styles/AppStyles';
import { useSnackbar } from '../Context/SnackbarProvider';
import { COLORS } from '../Styles/Colors';
import { useSearchParams } from 'react-router-dom';
import { ItemTypeChip } from '../Components/ItemType/ItemType';
import {
  SEARCHABLE_SELECT_WIDTH,
  defaultOptionSx,
  selectFocusSx,
  selectedOptionSx,
} from '../Components/Select/SearchableSelectShared';

type AspectFilter = 'all' | 'abatix' | 'non-abatix' | 'assets';

type Threshold = number | null;

type BinContentsRow = {
  aspect: 'abatix' | 'non-abatix' | 'asset';
  sourceId: number;
  identifier: string;
  description: string;
  binLocationId: number | null;
  binLocationName: string | null;
  uom: string | null;
  quantity: number;
  primaryQuantity: number;
  quantityMinimum: Threshold;
  quantityMaximum: Threshold;
  ovid: number;
  locationName: string | null;
  customerName: string | null;
};

type BinField = 'quantity' | 'quantityMinimum' | 'quantityMaximum';
type RowEdits = Partial<Record<BinField, string>>;

// Header search boxes; names match the API's query params.
const SEARCH_FIELDS = ['identifier', 'description', 'binLocationName', 'uom', 'locationName'];

type BinOption = {
  bin_location_id: number;
  bin_location_name: string;
  active_flag?: number | boolean;
  deleted_flag?: number | boolean;
};

const buildQuery = (params: Record<string, string | number | boolean | undefined>) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    search.set(key, String(value));
  });
  return search.toString();
};

const isNamedInventoryBin = (row: BinContentsRow) =>
  (row.aspect === 'abatix' || row.aspect === 'non-abatix') && Number(row.binLocationId) > 0;

const cellRowSx = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  width: '100%',
  height: '100%',
};

const qtyIconButtonSx = (danger?: boolean) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 20,
  height: 20,
  padding: 0,
  border: 'none',
  borderRadius: '4px',
  background: 'transparent',
  cursor: 'pointer',
  color: danger ? COLORS.red : COLORS.teal,
  opacity: 0.5,
  transition: 'opacity 0.2s ease, background-color 0.2s ease',
  '&:hover': {
    opacity: 1,
    backgroundColor: danger ? `${COLORS.red}18` : `${COLORS.teal}18`,
  },
  '&:focus-visible': {
    opacity: 1,
    outline: `2px solid ${danger ? COLORS.red : COLORS.teal}88`,
  },
  '&:disabled': { opacity: 0.3, cursor: 'default' },
});

const rowKey = (row: Pick<BinContentsRow, 'aspect' | 'sourceId' | 'binLocationId'>) =>
  `${row.aspect}-${row.sourceId}-${row.binLocationId ?? 'none'}`;

const readOnlyReason = (row: BinContentsRow, canEdit: boolean) => {
  if (row.aspect === 'asset') return 'Assets are tracked individually. Quantity cannot be edited.';
  if (Number(row.binLocationId) === 0) {
    return row.quantity < 0
      ? 'Bins hold more than on hand. Lower a bin to correct it.'
      : 'Primary bin. Editing another bin moves quantity here.';
  }
  if (!canEdit) return 'You do not have permission to edit bin quantities.';
  return 'Not editable';
};

const readOnlyThresholdReason = (row: BinContentsRow, canEdit: boolean) => {
  if (row.aspect === 'asset') return 'Assets do not have min/max levels.';
  if (Number(row.binLocationId) === 0) return 'Primary bin. Min and Max are set on named bins.';
  if (!canEdit) return 'You do not have permission to edit bin min/max.';
  return 'Not editable';
};

const parseWholeQuantity = (value: string): number | null => {
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  return Number(trimmed);
};

const parseThreshold = (value: string): Threshold | 'invalid' => {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  if (!/^\d+$/.test(trimmed)) return 'invalid';
  return Number(trimmed);
};

const thresholdToInput = (value: Threshold): string =>
  value === null || value === undefined ? '' : String(value);

const formatThreshold = (value: Threshold) =>
  value === null || value === undefined ? '—' : String(value);

const BinQtyCell: React.FC<{
  row: BinContentsRow;
  canEdit: boolean;
  isActive: boolean;
  isEdited: boolean;
  displayValue: string;
  saving: boolean;
  autoFocus: boolean;
  onStartEdit: () => void;
  onChange: (value: string) => void;
  onStopEdit: () => void;
  onDelete: () => void;
}> = ({
  row,
  canEdit,
  isActive,
  isEdited,
  displayValue,
  saving,
  autoFocus,
  onStartEdit,
  onChange,
  onStopEdit,
  onDelete,
}) => {
  const isNegative = row.quantity < 0;

  if (!canEdit || !isNamedInventoryBin(row)) {
    return (
      <Tooltip title={readOnlyReason(row, canEdit)} placement="top" arrow>
        <Box sx={{ ...cellRowSx, cursor: 'default' }}>
          <span style={isNegative ? { color: COLORS.red, fontWeight: 600 } : undefined}>
            {row.quantity}
          </span>
          {isNegative
            ? <AlertTriangle size={13} color={COLORS.red} />
            : <Lock size={12} color={`${COLORS.lightGray}99`} />}
        </Box>
      </Tooltip>
    );
  }

  const primaryAvailable = Number.isFinite(Number(row.primaryQuantity))
    ? Number(row.primaryQuantity)
    : 0;
  const maxQuantity = row.quantity + primaryAvailable;

  const trashButton = (
    <Box
      component="button"
      type="button"
      className="qty-edit-icon"
      aria-label={`Clear quantity of ${row.identifier} in this bin`}
      title={row.quantity === 0 ? 'Quantity is already 0' : 'Clear quantity (bin stays listed at 0)'}
      disabled={saving || row.quantity === 0}
      onClick={(event) => {
        event.stopPropagation();
        onDelete();
      }}
      onMouseDown={(event) => event.stopPropagation()}
      sx={qtyIconButtonSx(true)}
    >
      <Trash2 size={12} />
    </Box>
  );

  if (isActive) {
    return (
      <Box sx={cellRowSx}>
        {isEdited && (
          <span style={{ fontSize: '8px', color: COLORS.teal, fontWeight: 'bold', flexShrink: 0 }}>●</span>
        )}
        <Box
          component="input"
          type="number"
          autoFocus={autoFocus}
          min={0}
          max={Math.max(maxQuantity, row.quantity, 0)}
          step={1}
          disabled={saving}
          value={displayValue}
          title={`Max ${maxQuantity} (${primaryAvailable} available in primary bin)`}
          onClick={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
          onFocus={(event: React.FocusEvent<HTMLInputElement>) => event.currentTarget.select()}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
          onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
            event.stopPropagation();
            if (event.key === '-' || event.key === 'e' || event.key === 'E' || event.key === '+') {
              event.preventDefault();
              return;
            }
            if (event.key === 'Enter' || event.key === 'Escape') {
              event.preventDefault();
              onStopEdit();
            }
          }}
          sx={{
            width: 72,
            height: 30,
            boxSizing: 'border-box',
            borderRadius: '4px',
            padding: '4px 6px',
            outline: 'none',
            font: 'inherit',
            color: 'inherit',
            border: isEdited ? `1px solid ${COLORS.teal}` : `1px solid ${COLORS.teal}88`,
            boxShadow: `0 0 0 3px ${COLORS.teal}25`,
            backgroundColor: COLORS.white,
          }}
        />
        {trashButton}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        ...cellRowSx,
        mx: '-8px',
        px: '8px',
        width: 'calc(100% + 16px)',
        cursor: 'pointer',
        borderRadius: '4px',
        backgroundColor: isEdited ? `${COLORS.teal}15` : 'transparent',
        transition: 'background-color 0.2s ease',
        '& .qty-edit-icon': { opacity: 0.45, transition: 'opacity 0.2s ease' },
        '&:hover': { backgroundColor: isEdited ? `${COLORS.teal}15` : `${COLORS.teal}0d` },
        '&:hover .qty-edit-icon, &:focus-within .qty-edit-icon': { opacity: 1 },
      }}
    >
      <Tooltip title={`Click to edit. Max ${maxQuantity} (${primaryAvailable} available in primary bin).`} placement="top" arrow>
        <Box
          role="button"
          tabIndex={0}
          onClick={(event) => {
            event.stopPropagation();
            onStartEdit();
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') onStartEdit();
          }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            minWidth: 0,
            flex: 1,
            '&:focus-visible': { outline: `2px solid ${COLORS.teal}55` },
          }}
        >
          {isEdited && (
            <span style={{ fontSize: '8px', color: COLORS.teal, fontWeight: 'bold', flexShrink: 0 }}>●</span>
          )}
          <span style={{ fontWeight: 600, color: COLORS.teal }}>{displayValue}</span>
        </Box>
      </Tooltip>
      <Box
        component="button"
        type="button"
        className="qty-edit-icon"
        aria-label={`Edit quantity for ${row.identifier}`}
        title="Edit quantity"
        disabled={saving}
        onClick={(event) => {
          event.stopPropagation();
          onStartEdit();
        }}
        onMouseDown={(event) => event.stopPropagation()}
        sx={qtyIconButtonSx()}
      >
        <Pencil size={12} />
      </Box>
      {trashButton}
    </Box>
  );
};

const BinThresholdCell: React.FC<{
  row: BinContentsRow;
  field: 'quantityMinimum' | 'quantityMaximum';
  label: 'Min' | 'Max';
  canEdit: boolean;
  isActive: boolean;
  isEdited: boolean;
  displayValue: string;
  invalid: boolean;
  saving: boolean;
  autoFocus: boolean;
  onStartEdit: () => void;
  onChange: (value: string) => void;
  onStopEdit: () => void;
}> = ({
  row,
  field,
  label,
  canEdit,
  isActive,
  isEdited,
  displayValue,
  invalid,
  saving,
  autoFocus,
  onStartEdit,
  onChange,
  onStopEdit,
}) => {
  if (!canEdit || !isNamedInventoryBin(row)) {
    return (
      <Tooltip title={readOnlyThresholdReason(row, canEdit)} placement="top" arrow>
        <Box sx={{ ...cellRowSx, cursor: 'default' }}>
          <span style={{ color: isNamedInventoryBin(row) ? undefined : `${COLORS.lightGray}` }}>
            {formatThreshold(row[field])}
          </span>
        </Box>
      </Tooltip>
    );
  }

  if (isActive) {
    return (
      <Box sx={cellRowSx}>
        {isEdited && (
          <span style={{ fontSize: '8px', color: COLORS.teal, fontWeight: 'bold', flexShrink: 0 }}>●</span>
        )}
        <Box
          component="input"
          type="number"
          autoFocus={autoFocus}
          min={0}
          step={1}
          disabled={saving}
          value={displayValue}
          placeholder="—"
          title={`${label} for this bin. Leave blank if unset.`}
          onClick={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
          onFocus={(event: React.FocusEvent<HTMLInputElement>) => event.currentTarget.select()}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
          onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
            event.stopPropagation();
            if (event.key === '-' || event.key === 'e' || event.key === 'E' || event.key === '+') {
              event.preventDefault();
              return;
            }
            if (event.key === 'Enter' || event.key === 'Escape') {
              event.preventDefault();
              onStopEdit();
            }
          }}
          sx={{
            width: 64,
            height: 30,
            boxSizing: 'border-box',
            borderRadius: '4px',
            padding: '4px 6px',
            outline: 'none',
            font: 'inherit',
            color: 'inherit',
            border: invalid
              ? `1px solid ${COLORS.red}`
              : isEdited
                ? `1px solid ${COLORS.teal}`
                : `1px solid ${COLORS.teal}88`,
            boxShadow: `0 0 0 3px ${invalid ? `${COLORS.red}25` : `${COLORS.teal}25`}`,
            backgroundColor: invalid ? '#fdecea' : COLORS.white,
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        ...cellRowSx,
        mx: '-8px',
        px: '8px',
        width: 'calc(100% + 16px)',
        cursor: 'pointer',
        borderRadius: '4px',
        backgroundColor: isEdited ? `${COLORS.teal}15` : 'transparent',
        transition: 'background-color 0.2s ease',
        '& .qty-edit-icon': { opacity: 0.45, transition: 'opacity 0.2s ease' },
        '&:hover': { backgroundColor: isEdited ? `${COLORS.teal}15` : `${COLORS.teal}0d` },
        '&:hover .qty-edit-icon, &:focus-within .qty-edit-icon': { opacity: 1 },
      }}
    >
      <Tooltip title={`Click to edit ${label}. Leave blank if unset.`} placement="top" arrow>
        <Box
          role="button"
          tabIndex={0}
          onClick={(event) => {
            event.stopPropagation();
            onStartEdit();
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') onStartEdit();
          }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            minWidth: 0,
            flex: 1,
            '&:focus-visible': { outline: `2px solid ${COLORS.teal}55` },
          }}
        >
          {isEdited && (
            <span style={{ fontSize: '8px', color: COLORS.teal, fontWeight: 'bold', flexShrink: 0 }}>●</span>
          )}
          <span style={{ fontWeight: 600, color: COLORS.teal }}>
            {displayValue === '' ? '—' : displayValue}
          </span>
        </Box>
      </Tooltip>
      <Box
        component="button"
        type="button"
        className="qty-edit-icon"
        aria-label={`Edit ${label} for ${row.identifier}`}
        title={`Edit ${label}`}
        disabled={saving}
        onClick={(event) => {
          event.stopPropagation();
          onStartEdit();
        }}
        onMouseDown={(event) => event.stopPropagation()}
        sx={qtyIconButtonSx()}
      >
        <Pencil size={12} />
      </Box>
    </Box>
  );
};

interface BinContentsPageProps {
  /** Lock the page to one item type (e.g. the Non-ABATIX tab's View by Bin): hides the type toggle and Type column. */
  lockedAspect?: Exclude<AspectFilter, 'all'>;
}

const BinContentsPage: React.FC<BinContentsPageProps> = ({ lockedAspect }) => {
  const token: any = decode();
  const { showSnackbar } = useSnackbar();

  const [ovs] = useGetCustomData('/api/location/user/ovs');
  const [selectedOvs, setSelectedOvs] = useState<string>('');
  const [selectedBinId, setSelectedBinId] = useState<string>('');
  // `?type=` lets links (e.g. the old Non-ABATIX "View by Bin" address) open pre-filtered.
  const [urlParams] = useSearchParams();
  const [aspect, setAspect] = useState<AspectFilter>(() => {
    if (lockedAspect) return lockedAspect;
    const fromUrl = urlParams.get('type');
    return fromUrl === 'abatix' || fromUrl === 'non-abatix' || fromUrl === 'assets' ? fromUrl : 'all';
  });
  const [includeUnassigned, setIncludeUnassigned] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [sortField, setSortField] = useState('identifier');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [rows, setRows] = useState<BinContentsRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editedCells, setEditedCells] = useState<Record<string, RowEdits>>({});
  const [isEditAllMode, setIsEditAllMode] = useState(false);
  const [activeEditRowId, setActiveEditRowId] = useState<string | null>(null);
  const [activeEditField, setActiveEditField] = useState<BinField | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<BinContentsRow | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const canEditQty = Number(token?.roles?.level) >= 50;
  const { columnSearches, setSearch, clearSearch } = useColumnSearches(SEARCH_FIELDS);

  const handleSearchChange = useCallback((field: string, value: string) => {
    setSearch(field, value);
    setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClearSearch = useCallback((field: string) => {
    clearSearch(field);
    setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const searchParams = useMemo(
    () => Object.fromEntries(
      Object.entries(columnSearches).map(([field, value]) => [field, value.trim() || undefined]),
    ),
    [columnSearches],
  );

  const binsUrl = selectedOvs ? `/api/bins/${selectedOvs}` : '';
  const [bins] = useGetCustomData(binsUrl);

  useEffect(() => {
    if (token.roles.id !== USER_ROLE.ADMIN_ABATIX && ovs.length > 0 && !selectedOvs) {
      setSelectedOvs(String(ovs[0].OVID));
    }
  }, [token.roles.id, ovs, selectedOvs]);

  useEffect(() => {
    setSelectedBinId('');
    setPage(0);
  }, [selectedOvs]);

  const activeBins: BinOption[] = useMemo(() => {
    if (!Array.isArray(bins)) return [];
    return bins.filter((bin: BinOption) => {
      const active = Number(bin.active_flag) === 1 || bin.active_flag === true || bin.active_flag === undefined;
      const deleted = Number(bin.deleted_flag) === 1 || bin.deleted_flag === true;
      return active && !deleted;
    });
  }, [bins]);

  const queryString = useMemo(() => {
    if (!selectedOvs) return '';
    return buildQuery({
      ovid: selectedOvs,
      binLocationId: selectedBinId || undefined,
      aspect,
      includeUnassigned: includeUnassigned ? 'true' : undefined,
      ...searchParams,
      limit: pageSize,
      offset: page * pageSize,
      sortField,
      sortOrder,
    });
  }, [
    selectedOvs,
    selectedBinId,
    aspect,
    includeUnassigned,
    searchParams,
    page,
    pageSize,
    sortField,
    sortOrder,
  ]);

  const fetchContents = useCallback(async () => {
    if (!queryString) {
      setRows([]);
      setTotal(0);
      return;
    }

    setLoading(true);
    try {
      const response = await apiClient(`/api/bin-contents?${queryString}`);
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || `Failed to load bin contents (${response.status})`);
      }
      const body = await response.json();
      setRows(Array.isArray(body.data) ? body.data : []);
      setTotal(Number(body.total) || 0);
    } catch (error: any) {
      console.error(error);
      setRows([]);
      setTotal(0);
      showSnackbar(error.message || 'Failed to load bin contents', 'error');
    } finally {
      setLoading(false);
    }
  }, [queryString, showSnackbar]);

  useEffect(() => {
    fetchContents();
  }, [fetchContents]);

  useEffect(() => {
    setEditedCells({});
    setActiveEditRowId(null);
    setActiveEditField(null);
    setIsEditAllMode(false);
    setDeleteTarget(null);
  }, [queryString]);

  const originalFieldValue = (row: BinContentsRow, field: BinField): string => {
    if (field === 'quantity') return String(row.quantity);
    return thresholdToInput(row[field]);
  };

  const handleFieldChange = (id: string, field: BinField, originalValue: string, value: string) => {
    if (value !== '' && !/^\d*$/.test(value)) return;
    setEditedCells((prev) => {
      const current = { ...(prev[id] || {}) };
      if (value === originalValue) {
        delete current[field];
      } else {
        current[field] = value;
      }
      if (Object.keys(current).length === 0) {
        const { [id]: _removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [id]: current };
    });
  };

  const handleCancelChanges = () => {
    setEditedCells({});
    setActiveEditRowId(null);
    setActiveEditField(null);
    setIsEditAllMode(false);
  };

  const collectEdits = () => {
    const items: {
      row: BinContentsRow;
      quantity?: number;
      quantityMinimum?: Threshold;
      quantityMaximum?: Threshold;
    }[] = [];

    for (const [id, edits] of Object.entries(editedCells)) {
      const row = rows.find((candidate) => rowKey(candidate) === id);
      if (!row || !isNamedInventoryBin(row)) continue;

      const item: (typeof items)[number] = { row };

      if (edits.quantity !== undefined) {
        const quantity = parseWholeQuantity(edits.quantity);
        if (quantity === null) {
          showSnackbar(`Quantity for ${row.identifier} must be a whole number of 0 or more`, 'error');
          return null;
        }
        if (quantity !== row.quantity) item.quantity = quantity;
      }

      if (edits.quantityMinimum !== undefined) {
        const minimum = parseThreshold(edits.quantityMinimum);
        if (minimum === 'invalid') {
          showSnackbar(`Min for ${row.identifier} must be a whole number of 0 or more, or blank`, 'error');
          return null;
        }
        item.quantityMinimum = minimum;
      }

      if (edits.quantityMaximum !== undefined) {
        const maximum = parseThreshold(edits.quantityMaximum);
        if (maximum === 'invalid') {
          showSnackbar(`Max for ${row.identifier} must be a whole number of 0 or more, or blank`, 'error');
          return null;
        }
        item.quantityMaximum = maximum;
      }

      const nextMin = item.quantityMinimum !== undefined ? item.quantityMinimum : row.quantityMinimum;
      const nextMax = item.quantityMaximum !== undefined ? item.quantityMaximum : row.quantityMaximum;
      if (nextMin !== null && nextMax !== null && nextMin > nextMax) {
        showSnackbar(`Min cannot exceed Max for ${row.identifier}`, 'error');
        return null;
      }

      if (
        item.quantity !== undefined
        || item.quantityMinimum !== undefined
        || item.quantityMaximum !== undefined
      ) {
        items.push(item);
      }
    }
    return items;
  };

  const validateQuantityEdits = (items: { row: BinContentsRow; quantity?: number }[]) => {
    const groups = new Map<string, {
      primary: number;
      netChange: number;
      identifier: string;
      count: number;
      firstCurrent: number;
    }>();
    for (const { row, quantity } of items) {
      if (quantity === undefined) continue;
      const key = `${row.aspect}-${row.sourceId}`;
      const current = groups.get(key) || {
        primary: Number(row.primaryQuantity) || 0,
        netChange: 0,
        identifier: row.identifier,
        count: 0,
        firstCurrent: row.quantity,
      };
      current.netChange += quantity - row.quantity;
      current.count += 1;
      groups.set(key, current);
    }

    for (const group of Array.from(groups.values())) {
      if (group.netChange > 0 && group.netChange > group.primary) {
        if (group.count === 1) {
          showSnackbar(
            `Quantity cannot be greater than what is available in the primary bin. This bin can be at most ${group.firstCurrent + group.primary}.`,
            'error',
          );
        } else {
          showSnackbar(
            `${group.identifier}: these bins together need ${group.netChange} more than the primary bin has available (${Math.max(group.primary, 0)}).`,
            'error',
          );
        }
        return false;
      }
    }
    return true;
  };

  const handleSaveChanges = async () => {
    const items = collectEdits();
    if (!items) return;
    if (items.length === 0) {
      handleCancelChanges();
      return;
    }
    if (!validateQuantityEdits(items)) return;

    setSaving(true);
    try {
      const response = await apiClient('/api/bin-contents/quantity/bulk', {
        method: 'PUT',
        body: JSON.stringify({
          items: items.map(({ row, quantity, quantityMinimum, quantityMaximum }) => ({
            aspect: row.aspect,
            sourceId: row.sourceId,
            binLocationId: row.binLocationId,
            ...(quantity !== undefined ? { quantity } : {}),
            ...(quantityMinimum !== undefined ? { quantityMinimum } : {}),
            ...(quantityMaximum !== undefined ? { quantityMaximum } : {}),
          })),
        }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(body.error || 'Failed to update bins');
      }

      const quantityItems = items.filter((item) => item.quantity !== undefined) as { row: BinContentsRow; quantity: number }[];
      const movedToPrimary = quantityItems.reduce((sum, { row, quantity }) => (
        quantity < row.quantity ? sum + (row.quantity - quantity) : sum
      ), 0);
      const movedFromPrimary = quantityItems.reduce((sum, { row, quantity }) => (
        quantity > row.quantity ? sum + (quantity - row.quantity) : sum
      ), 0);
      const results = Array.isArray(body.data) ? body.data : [];
      const raised = new Set(
        results
          .filter((result: { adjusted?: { minimum?: boolean; maximum?: boolean }; sourceId?: number }) =>
            result.adjusted?.minimum || result.adjusted?.maximum)
          .map((result: { sourceId?: number }) => result.sourceId),
      );

      let message = `Updated ${items.length} bin ${items.length === 1 ? 'row' : 'rows'}`;
      if (movedToPrimary && !movedFromPrimary) {
        message = `Moved ${movedToPrimary} to the primary bin`;
      } else if (movedFromPrimary && !movedToPrimary) {
        message = `Moved ${movedFromPrimary} from the primary bin`;
      }
      if (raised.size > 0) {
        message += `. Location Min/Max raised for ${raised.size} item${raised.size === 1 ? '' : 's'} because bin totals exceeded them`;
      }
      showSnackbar(message, 'success');

      setEditedCells({});
      setActiveEditRowId(null);
      setActiveEditField(null);
      setIsEditAllMode(false);
      await fetchContents();
    } catch (error: any) {
      showSnackbar(error.message || 'Failed to update bins', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    const row = deleteTarget;
    setIsDeleting(true);
    try {
      const response = await apiClient('/api/bin-contents/quantity', {
        method: 'PUT',
        body: JSON.stringify({
          aspect: row.aspect,
          sourceId: row.sourceId,
          binLocationId: row.binLocationId,
          quantity: 0,
        }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(body.error || 'Failed to clear bin quantity');
      }

      const key = rowKey(row);
      setEditedCells((prev) => {
        const current = prev[key];
        if (!current) return prev;
        const { quantity: _removed, ...restFields } = current;
        if (Object.keys(restFields).length === 0) {
          const { [key]: _row, ...rest } = prev;
          return rest;
        }
        return { ...prev, [key]: restFields };
      });
      if (activeEditRowId === key) {
        setActiveEditRowId(null);
        setActiveEditField(null);
      }
      showSnackbar(
        row.quantity > 0
          ? `Moved ${row.quantity} to the primary bin. This bin is still listed at 0.`
          : 'Quantity is already 0',
        'success',
      );
      setDeleteTarget(null);
      await fetchContents();
    } catch (error: any) {
      showSnackbar(error.message || 'Failed to clear bin quantity', 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSortChange = (field: string, order: 'asc' | 'desc' | null) => {
    setSortField(field || 'identifier');
    setSortOrder(order ?? 'asc');
    setPage(0);
  };

  const handleExport = async () => {
    if (!selectedOvs) {
      showSnackbar('Select a location first', 'error');
      return;
    }

    setExporting(true);
    try {
      const qs = buildQuery({
        ovid: selectedOvs,
        binLocationId: selectedBinId || undefined,
        aspect,
        includeUnassigned: includeUnassigned ? 'true' : undefined,
        ...searchParams,
        sortField,
        sortOrder,
      });
      const response = await apiClient(`/api/bin-contents/export?${qs}`);
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || 'Export failed');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `view-by-bin-${lockedAspect ? `${lockedAspect}-` : ''}ovid-${selectedOvs}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      showSnackbar(error.message || 'Export failed', 'error');
    } finally {
      setExporting(false);
    }
  };

  const gridRows = useMemo(
    () =>
      rows.map((row) => ({
        ...row,
        id: `${row.aspect}-${row.sourceId}-${row.binLocationId ?? 'none'}`,
      })),
    [rows],
  );

  const hideBinColumn = Boolean(selectedBinId);

  const searchHeader = (field: string, headerName: string) => (
    <SearchableHeader
      field={field}
      headerName={headerName}
      searchValue={columnSearches[field]}
      onSearchChange={handleSearchChange}
      onClearSearch={handleClearSearch}
    />
  );

  const startRowEdit = (id: string, field: BinField) => {
    setActiveEditRowId(id);
    setActiveEditField(field);
  };

  const currentThreshold = (row: BinContentsRow, field: 'quantityMinimum' | 'quantityMaximum'): Threshold => {
    const edited = editedCells[rowKey(row)]?.[field];
    if (edited === undefined) return row[field];
    const parsed = parseThreshold(edited);
    return parsed === 'invalid' ? row[field] : parsed;
  };

  const isMinMaxInvalid = (row: BinContentsRow) => {
    const min = currentThreshold(row, 'quantityMinimum');
    const max = currentThreshold(row, 'quantityMaximum');
    return min !== null && max !== null && min > max;
  };

  const editableHeader = (label: string) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        width: '100%',
        height: '100%',
        pt: '12px',
        ...(isEditAllMode ? { backgroundColor: `${COLORS.teal}08` } : {}),
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <span style={{
          fontSize: '0.7rem',
          fontWeight: 700,
          textTransform: 'uppercase',
          color: '#64748b',
          letterSpacing: '0.06em',
          lineHeight: 1,
        }}
        >
          {label}
        </span>
        {canEditQty && (
          <span
            style={{ fontSize: '10px', color: COLORS.teal, cursor: 'help', lineHeight: 1 }}
            title={isEditAllMode ? 'All editable rows are open' : 'Click a teal value or pencil to edit'}
          >
            ✎
          </span>
        )}
      </Box>
    </Box>
  );

  const columns: ColumnDef<(typeof gridRows)[number]>[] = [
    {
      field: 'identifier',
      headerName: 'Part No.',
      width: 160,
      sortable: true,
      renderHeader: () => searchHeader('identifier', 'Part No.'),
    },
    {
      field: 'description',
      headerName: 'Description',
      width: 260,
      sortable: true,
      renderHeader: () => searchHeader('description', 'Description'),
    },
    ...(!hideBinColumn
      ? [{
          field: 'binLocationName',
          headerName: 'Bin',
          width: 180,
          sortable: true,
          renderHeader: () => searchHeader('binLocationName', 'Bin'),
          renderCell: (params: any) => params.row.binLocationName || (
            <span style={{ color: COLORS.lightGray, fontStyle: 'italic' }}>
              {Number(params.row.binLocationId) === 0 ? 'Primary' : 'Unassigned'}
            </span>
          ),
        } as ColumnDef<(typeof gridRows)[number]>]
      : []),
    {
      field: 'uom',
      headerName: 'UOM',
      width: 110,
      sortable: true,
      renderHeader: () => searchHeader('uom', 'UOM'),
      renderCell: (params) => params.row.uom || (
        <span style={{ color: COLORS.lightGray }}>—</span>
      ),
    },
    {
      field: 'quantity',
      headerName: 'Qty in Bin',
      width: 190,
      sortable: true,
      renderHeader: () => editableHeader('Qty in Bin'),
      renderCell: (params) => {
        const id = String(params.row.id);
        const displayValue = editedCells[id]?.quantity ?? String(params.row.quantity);
        return (
          <BinQtyCell
            row={params.row}
            canEdit={canEditQty}
            isActive={canEditQty && (isEditAllMode || activeEditRowId === id)}
            isEdited={editedCells[id]?.quantity !== undefined}
            displayValue={displayValue}
            saving={saving || isDeleting}
            autoFocus={!isEditAllMode && activeEditRowId === id && activeEditField === 'quantity'}
            onStartEdit={() => startRowEdit(id, 'quantity')}
            onChange={(value) => handleFieldChange(id, 'quantity', originalFieldValue(params.row, 'quantity'), value)}
            onStopEdit={() => { setActiveEditRowId(null); setActiveEditField(null); }}
            onDelete={() => setDeleteTarget(params.row)}
          />
        );
      },
    },
    {
      field: 'quantityMinimum',
      headerName: 'Min QTY',
      width: 110,
      sortable: true,
      renderHeader: () => editableHeader('Min QTY'),
      renderCell: (params) => {
        const id = String(params.row.id);
        const displayValue = editedCells[id]?.quantityMinimum ?? thresholdToInput(params.row.quantityMinimum);
        return (
          <BinThresholdCell
            row={params.row}
            field="quantityMinimum"
            label="Min"
            canEdit={canEditQty}
            isActive={canEditQty && (isEditAllMode || activeEditRowId === id)}
            isEdited={editedCells[id]?.quantityMinimum !== undefined}
            displayValue={displayValue}
            invalid={isMinMaxInvalid(params.row)}
            saving={saving || isDeleting}
            autoFocus={!isEditAllMode && activeEditRowId === id && activeEditField === 'quantityMinimum'}
            onStartEdit={() => startRowEdit(id, 'quantityMinimum')}
            onChange={(value) => handleFieldChange(id, 'quantityMinimum', originalFieldValue(params.row, 'quantityMinimum'), value)}
            onStopEdit={() => { setActiveEditRowId(null); setActiveEditField(null); }}
          />
        );
      },
    },
    {
      field: 'quantityMaximum',
      headerName: 'Max QTY',
      width: 110,
      sortable: true,
      renderHeader: () => editableHeader('Max QTY'),
      renderCell: (params) => {
        const id = String(params.row.id);
        const displayValue = editedCells[id]?.quantityMaximum ?? thresholdToInput(params.row.quantityMaximum);
        return (
          <BinThresholdCell
            row={params.row}
            field="quantityMaximum"
            label="Max"
            canEdit={canEditQty}
            isActive={canEditQty && (isEditAllMode || activeEditRowId === id)}
            isEdited={editedCells[id]?.quantityMaximum !== undefined}
            displayValue={displayValue}
            invalid={isMinMaxInvalid(params.row)}
            saving={saving || isDeleting}
            autoFocus={!isEditAllMode && activeEditRowId === id && activeEditField === 'quantityMaximum'}
            onStartEdit={() => startRowEdit(id, 'quantityMaximum')}
            onChange={(value) => handleFieldChange(id, 'quantityMaximum', originalFieldValue(params.row, 'quantityMaximum'), value)}
            onStopEdit={() => { setActiveEditRowId(null); setActiveEditField(null); }}
          />
        );
      },
    },
    {
      field: 'locationName',
      headerName: 'Location',
      width: 180,
      sortable: true,
      renderHeader: () => searchHeader('locationName', 'Location'),
    },
    // Type sits on the right, matching View Inventory and Transactions.
    ...(!lockedAspect
      ? [{
          field: 'aspect',
          headerName: 'Type',
          width: 118,
          sortable: true,
          renderCell: (params: any) => (
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <ItemTypeChip type={params.row.aspect as BinContentsRow['aspect']} />
            </Box>
          ),
        } as ColumnDef<(typeof gridRows)[number]>]
      : []),
  ];

  const hasEdits = Object.keys(editedCells).length > 0;

  return (
    <PageContainer>
      <Header
        title="View by Bin"
        subtitle={selectedOvs
          ? undefined
          : lockedAspect === 'non-abatix'
            ? 'Select a location to view Non-ABATIX bin contents.'
            : 'Select a location to view bin contents across ABATIX, Non-ABATIX, and Assets.'}
        count={total}
        hideCount={false}
      />

      {deleteTarget && (
        <ConfirmationModal
          title="Clear bin quantity?"
          message={`Clear ${deleteTarget.identifier} in ${deleteTarget.binLocationName || 'this bin'} to 0? The quantity (${deleteTarget.quantity}) will be moved to the primary bin. This bin will stay listed.`}
          confirmText="Clear Quantity"
          variant="danger"
          isLoading={isDeleting}
          loadingText="Clearing..."
          onConfirm={handleConfirmDelete}
          onCancel={() => { if (!isDeleting) setDeleteTarget(null); }}
        />
      )}

      <DataTable
        data={gridRows}
        columns={columns}
        pageKey={lockedAspect ? `view-by-bin-${lockedAspect}` : 'view-by-bin'}
        loading={loading || exporting || saving}
        initialPageSize={pageSize}
        idField="id"
        serverSidePagination={true}
        page={page}
        rowCount={total}
        onPageChange={setPage}
        // DataTable reports page size on every page change too; resetting the
        // page unconditionally here is what kept "next page" stuck on page 1.
        onPageSizeChange={(size) => {
          if (size === pageSize) return;
          setPageSize(size);
          setPage(0);
        }}
        hideColumnFilters={true}
        hideSearchBar={true}
        serverSideSort={true}
        defaultSort={[{ field: 'identifier', sort: 'asc' }]}
        onSortModelChange={handleSortChange}
        onMergeExport={selectedOvs ? handleExport : undefined}
        mergeExportLabel="Export All Rows (CSV)"
        onEditAll={canEditQty ? () => { setIsEditAllMode(true); setActiveEditRowId(null); setActiveEditField(null); } : undefined}
        editAllTooltip={lockedAspect === 'non-abatix'
          ? 'Edit Qty, Min, and Max for all rows on this page'
          : 'Edit Qty, Min, and Max for all ABATIX and Non-ABATIX rows on this page'}
        isEditAllMode={isEditAllMode}
        isRowEditMode={!!activeEditRowId}
        hasEdits={hasEdits}
        editedCount={Object.keys(editedCells).length}
        onSaveChanges={handleSaveChanges}
        onCancelChanges={handleCancelChanges}
      >
        <LocationSelect
          ovs={ovs}
          selectedOvs={selectedOvs}
          onChange={(value) => {
            setSelectedOvs(value === '' || value === null || value === undefined ? '' : String(value));
            setPage(0);
          }}
          defaultName="Select Location"
          setDefault={false}
        />

        <Select
          displayEmpty
          value={selectedBinId}
          onChange={(e) => {
            setSelectedBinId(String(e.target.value));
            // The Bin column hides once one bin is picked; don't leave a hidden filter on it.
            if (e.target.value) clearSearch('binLocationName');
            setPage(0);
          }}
          disabled={!selectedOvs}
          size="small"
          sx={{
            minWidth: { xs: 0, sm: SEARCHABLE_SELECT_WIDTH },
            width: { xs: '100%', sm: 'auto' },
            maxWidth: '100%',
            ...selectFocusSx,
          }}
        >
          <MenuItem value="" sx={defaultOptionSx}>
            <em>All Bins</em>
          </MenuItem>
          {activeBins.map((bin) => (
            <MenuItem key={bin.bin_location_id} value={String(bin.bin_location_id)} sx={selectedOptionSx}>
              {bin.bin_location_name || `Bin ${bin.bin_location_id}`}
            </MenuItem>
          ))}
        </Select>

        {!lockedAspect && (
        <ToggleButtonGroup
          exclusive
          size="small"
          value={aspect}
          onChange={(_e, value: AspectFilter | null) => {
            if (!value) return;
            setAspect(value);
            setPage(0);
          }}
          sx={{
            height: 40,
            '& .MuiToggleButton-root': {
              px: 1.5,
              fontSize: '0.875rem',
              fontWeight: 500,
              textTransform: 'none',
              color: COLORS.gray,
              borderColor: `${COLORS.lightGray}55`,
              '&:hover': { backgroundColor: `${COLORS.teal}10` },
            },
            '& .MuiToggleButton-root.Mui-selected': {
              backgroundColor: COLORS.teal,
              color: COLORS.white,
              '&:hover': { backgroundColor: COLORS.teal },
            },
          }}
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="abatix">ABATIX</ToggleButton>
          <ToggleButton value="non-abatix">Non-ABATIX</ToggleButton>
          <ToggleButton value="assets">Assets</ToggleButton>
        </ToggleButtonGroup>
        )}

        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={includeUnassigned}
              onChange={(e) => {
                setIncludeUnassigned(e.target.checked);
                setPage(0);
              }}
              disabled={Boolean(selectedBinId)}
              sx={{ color: COLORS.lightGray, '&.Mui-checked': { color: COLORS.teal } }}
            />
          }
          label="Include unassigned / Primary Bin"
          sx={{ ml: 0, '& .MuiFormControlLabel-label': { fontSize: '0.875rem', color: COLORS.gray } }}
        />
      </DataTable>
    </PageContainer>
  );
};

export default BinContentsPage;
